<template>
  <BaseSlideOverModal
    v-model:is-modal-open="localOpen"
    width="2xl"
    :footer="false"
  >
    <template #body>
      <div class="flex flex-col w-full gap-2 mx-2 overflow-auto">
        <div class="w-full flex-col gap-3">
          <SelectComponent
            v-if="categories && categories.length > 0"
            v-model="selectedCategory"
            :values="categories"
            :label="$t('global.categories')"
            name=""
          />
          <h1 class="text-xl font-bold">
            {{ categoryName }}
          </h1>
        </div>
        <div class="flex">
          <BaseSearchInput v-model="search_term" />
        </div>
        <div
          v-for="(item, idx) in filteredItems"
          :key="idx"
          class="p-3 pt-2 w-full border border-gray rounded"
        >
          <div class="flex flex-col gap-1">
            <div class="flex flex-row">
              <h3
                class="flex-1 truncate text-base font-semibold text-gray-700 capitalize-first"
              >
                {{ item.name_index }}
              </h3>
              <span class="shrink-0">
                <FontAwesomeIcon
                  v-if="item.favorite"
                  :icon="['fas', 'star']"
                  class="flex-shrink-0 text-yellow-300"
                />
              </span>
              <span
                class="flex shrink-0 select-none items-center gap-1 rounded border px-2 text-sm text-gray-700"
              >
                <span class="text-xs">HTVA</span>
                <span class="font-bold">{{
                  item[`default_purchase_price_htva`] ?? '0.00'
                }}</span>
              </span>
            </div>
            <div class="-mt-1 flex flex-col">
              <span v-if="item.ref" class="select-none text-xs text-gray-500"
                ># {{ item.ref }}</span
              >
              <p
                v-if="item.remarks"
                v-html="displayedRemarks(item.remarks)"
              ></p>
            </div>
            <div class="flex justify-between">
              <div class="flex flex-row items-center space-x-2">
                <div v-if="item.stockable" class="flex justify-between">
                  <div
                    class="flex flex-row gap-1.5 divide-x divide-slate-200 rounded bg-gray-100 px-1 py-0.5"
                  >
                    <div class="flex flex-row gap-1 px-1">
                      <span
                        ><FontAwesomeIcon :icon="['far', 'warehouse']"
                      /></span>
                      <span class="font-bold">{{
                        item.item_stocks.filter(
                          (item_stock) => item_stock.status === 'AVAILABLE'
                        )?.length
                      }}</span>
                    </div>
                    <div class="flex flex-row gap-1 px-1">
                      <span
                        ><FontAwesomeIcon :icon="['far', 'boxes-stacked']"
                      /></span>
                      <span class="font-bold">{{
                        item.item_stocks.filter(
                          (item_stock) => item_stock.status === 'IN_ORDER'
                        )?.length
                      }}</span>
                    </div>
                    <div class="flex flex-row gap-1 px-1">
                      <span><FontAwesomeIcon :icon="['far', 'tags']" /></span>
                      <span class="font-bold">{{
                        item.item_stocks.filter(
                          (item_stock) => item_stock.status === 'SOLD'
                        )?.length
                      }}</span>
                    </div>
                    <div
                      class="flex flex-1 flex-row items-end justify-between gap-2"
                    >
                      <div>
                        <button
                          v-if="item.variants?.length > 0"
                          class="rounded border border-gray-300 bg-secondary px-2 py-0.5 text-center text-xs text-contrast-secondary"
                          @click="
                            $emit('showVariants', [
                              { ...item, variants: [] },
                              ...item.variants
                            ])
                          "
                        >
                          <FontAwesomeIcon :icon="['fal', 'swatchbook']" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="flex flex-row gap-2">
                <div
                  v-if="supplierOrder.item_ids?.includes(item.id)"
                  class="flex shrink-0 flex-row items-center text-green-600"
                >
                  <FontAwesomeIcon :icon="['fas', 'circle-check']" size="lg" />
                </div>
                <input
                  v-model="item.quantity"
                  class="w-12 rounded border border-gray-300 px-1 py-0.5 text-center text-xs"
                  type="number"
                  min="1"
                  max="9999"
                />
                <BaseButton
                  :title="$t('button.add')"
                  icon="plus"
                  size="extra-small"
                  @click="
                    $emit('selected-item', { quantity: quantity, item: item })
                  "
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </BaseSlideOverModal>
</template>

<script>
import BaseSlideOverModal from '@c/addf-package/components/modal/BaseSlideOverModal.vue'
import BaseSearchInput from '@c/addf-package/components/BaseInput/BaseSearchInput.vue'
import { mapActions, mapGetters } from 'vuex'
import SelectComponent from '@c/BaseFormComponent/SelectComponent.vue'
import BaseButton from '@c/addf-package/components/BaseButton/BaseButton.vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'

export default {
  name: 'SupplierItemsModal',
  components: {
    FontAwesomeIcon,
    BaseButton,
    SelectComponent,
    BaseSearchInput,
    BaseSlideOverModal
  },
  props: {
    open: {
      type: Boolean,
      required: true
    },
    supplierOrder: {
      type: Object,
      required: true
    },
    canEditCategory: {
      type: Boolean,
      required: false,
      default: false
    },
    itemTypeAvailable: {
      type: Array,
      required: false,
      default: () => ['item', 'pack']
    },
    selectedItems: {
      type: Array,
      required: false,
      default: () => []
    },
    hideQuantityInput: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  emits: ['update:open', 'selected-item'],
  data() {
    return {
      search_term: '',
      items: [],
      categories: [],
      selectedCategory: null,
      categoryName: null
    }
  },
  computed: {
    ...mapGetters({
      initialMetadata: 'metadata/getInitialMetadata'
    }),
    localOpen: {
      get() {
        return this.open
      },
      set(value) {
        this.$emit('update:open', value)
      }
    },
    filteredItems() {
      let tempItems = this.items
      if (this.selectedCategory) {
        tempItems = tempItems.filter((item) => {
          return item.item_category_id === this.selectedCategory
        })
      }
      if (this.search_term?.length > 2) {
        const searchTermNormalized = this.normalizeString(this.search_term)
        tempItems = tempItems.filter((item) => {
          const nameNormalized = this.normalizeString(item.name_index)
          return nameNormalized.includes(searchTermNormalized)
        })
      }
      return tempItems
    }
  },
  mounted() {
    if (this.supplierOrder) {
      this.processFetchSupplierItemsListAction({
        supplier_id: this.supplierOrder?.recipient_id
      }).then((response) => {
        this.items = response.items.map((item) => {
          return {
            ...item,
            quantity: 1
          }
        })
        this.categories = response.metadata.categories.map((category) => {
          return {
            id: category.id,
            name: category.name[this.$i18n.locale]
          }
        })
      })
    }
  },
  methods: {
    ...mapActions({
      processFetchSupplierItemsListAction: 'supplier/fetchSupplierItemsList'
    }),
    normalizeString(str) {
      return str
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
    },
    displayedRemarks(remarks) {
      if (remarks[this.$i18n.locale]) return remarks[this.$i18n.locale]
      if (typeof remarks === String) return remarks
      return null
    }
  }
}
</script>
